import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { fmtBRL, fmtDate } from "../lib/format";

const VIA_W = 700;
const PRINT_HALF = 505;

const Row = ({ l, v, bg }) => (
  <div className={`px-3 py-1.5 flex items-start gap-3 ${bg}`}>
    <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-950 w-24 shrink-0 pt-0.5">{l}</span>
    <span className="text-[13px] text-slate-900 flex-1 min-w-0 break-words">{v || "—"}</span>
  </div>
);

const ViaBlock = ({ doc, items, discount, freight, subtotal, viaRef }) => (
  <div ref={viaRef} className="bg-white border border-slate-400" style={{ width: VIA_W }}>
    <div className="bg-blue-950 text-white px-3 py-2 flex items-center justify-between gap-4">
      <span className="font-semibold tracking-wide text-[13px]">PEDIDO Nº <span className="ml-1">#{doc.code}</span></span>
      <span className="text-[13px]"><span className="text-[10px] font-semibold uppercase tracking-wide mr-2">Data</span>{fmtDate(doc.order_date)}</span>
    </div>
    <div className="divide-y divide-slate-300">
      <Row l="Cliente" v={<b>{doc.client_name}</b>} bg="bg-slate-200" />
      <Row l="Endereço" v={doc.address} bg="bg-white" />
      <Row l="Cidade / Est." v={[doc.city, doc.state].filter(Boolean).join(" / ")} bg="bg-slate-200" />
      <Row l="CPF / CNPJ" v={doc.document} bg="bg-white" />
      {doc.notes && <Row l="Observações" v={doc.notes} bg="bg-slate-200" />}
    </div>
    <table className="w-full text-[12px] border-t border-slate-300">
      <thead>
        <tr className="bg-blue-950 text-white text-[10px] uppercase tracking-wide">
          <th className="px-2.5 py-1.5 font-semibold text-left w-14">Quant</th>
          <th className="px-2.5 py-1.5 font-semibold text-left">Descrição / Produto</th>
          <th className="px-2.5 py-1.5 font-semibold text-right w-24 whitespace-nowrap">Unitário</th>
          <th className="px-2.5 py-1.5 font-semibold text-right w-24 whitespace-nowrap">Total</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, idx) => (
          <tr key={`${it.description || "item"}-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-slate-100"}>
            <td className="px-2.5 py-1.5 text-slate-900">{it.quant}</td>
            <td className="px-2.5 py-1.5 text-slate-900 break-words">{it.description || doc.description || "Item"}</td>
            <td className="px-2.5 py-1.5 text-right text-slate-700 whitespace-nowrap">{fmtBRL(it.unit_price)}</td>
            <td className="px-2.5 py-1.5 text-right font-semibold text-slate-900 whitespace-nowrap">{fmtBRL((it.quant || 0) * (it.unit_price || 0))}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="border-t border-slate-300 bg-slate-50 px-3 py-2.5 flex items-end justify-between gap-6">
      <div className="text-center text-[11px] text-slate-600 pb-0.5" style={{ width: 220 }}>
        <div className="border-t border-slate-500 pt-1">Visto</div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-6">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-950">Subtotal</span>
          <span className="text-[13px] font-semibold text-slate-900 w-28 text-right">{fmtBRL(subtotal)}</span>
        </div>
        {discount > 0 && (
          <div className="flex items-center gap-6">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-950">Desconto</span>
            <span className="text-[13px] font-semibold text-rose-700 w-28 text-right">− {fmtBRL(discount)}</span>
          </div>
        )}
        {freight > 0 && (
          <div className="flex items-center gap-6">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-950">Frete</span>
            <span className="text-[13px] font-semibold text-slate-900 w-28 text-right">+ {fmtBRL(freight)}</span>
          </div>
        )}
        {doc.credit_applied_total > 0 && (
          <div className="flex items-center gap-6">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-950">Crédito aplicado</span>
            <span className="text-[13px] font-semibold text-amber-700 w-28 text-right">− {fmtBRL(doc.credit_applied_total)}</span>
          </div>
        )}
        <div className="bg-blue-950 text-white rounded px-4 py-1.5 text-right mt-0.5">
          <span className="text-[9px] uppercase tracking-[0.15em] text-blue-200 mr-3">Total Geral do Pedido</span>
          <span className="font-semibold text-[16px]">{fmtBRL(doc.total_value)}</span>
        </div>
        {doc.credit_applied_total > 0 && (
          <div className="bg-emerald-700 text-white rounded px-4 py-1 text-right">
            <span className="text-[9px] uppercase tracking-[0.15em] text-emerald-200 mr-3">Total a Pagar (desc. crédito)</span>
            <span className="font-semibold text-[14px]">{fmtBRL(doc.total_value - doc.credit_applied_total)}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const TwoViasPrint = ({ doc, items, discount, freight, subtotal, onClose }) => {
  const viaRef = useRef(null);
  const [dims, setDims] = useState({ scale: 1, h: 0 });

  useLayoutEffect(() => {
    if (viaRef.current) {
      const h = viaRef.current.offsetHeight;
      setDims({ scale: Math.min(1, PRINT_HALF / h), h });
    }
  }, [doc, items]);

  useEffect(() => {
    document.body.classList.add("print-2vias");
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => {
      document.body.classList.remove("print-2vias");
      window.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  const { scale, h } = dims;
  const slot = { width: VIA_W * scale, height: h ? h * scale : undefined };
  const inner = { transform: `scale(${scale})`, transformOrigin: "top left" };
  const viaProps = { doc, items, discount, freight, subtotal };

  return createPortal(
    <div className="twovias-overlay fixed inset-0 z-[300] bg-slate-300 overflow-auto" data-testid="twovias-overlay" style={{ colorScheme: "light" }}>
      <div className="no-print sticky top-0 z-10 bg-blue-950 text-white px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg">
        <div>
          <p className="font-semibold text-sm">Impressão em 2 vias — Pedido #{doc.code}</p>
          <p className="text-[11px] text-blue-200">As duas vias abaixo saem em uma única folha A4. Clique em Imprimir ou use Ctrl+P.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="twovias-print-now" onClick={() => window.print()}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Printer size={16} /> Imprimir
          </button>
          <button data-testid="twovias-back" onClick={onClose}
            className="flex items-center gap-2 border border-blue-300/40 text-blue-100 hover:bg-blue-900 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <ArrowLeft size={15} /> Voltar
          </button>
        </div>
      </div>
      <div id="print-2vias-area" className="py-5 flex flex-col items-center">
        <div style={slot}>
          <div style={inner}><ViaBlock {...viaProps} viaRef={viaRef} /></div>
        </div>
        <div className="twovias-cut border-t-2 border-dashed border-slate-500 my-3" style={{ width: VIA_W * scale }} />
        <div style={slot}>
          <div style={inner}><ViaBlock {...viaProps} /></div>
        </div>
      </div>
    </div>,
    document.body
  );
};
